import React, { useState, useEffect, useReducer } from "react";
import moment from "moment-timezone";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import {
  listGroupCampaigns,
  getGroupCampaign,
  deleteGroupCampaign,
  cancelGroupCampaign,
  stopGroupCampaign,
  restartGroupCampaign,
} from "../../services/groupCampaigns.api";
import toastError from "../../errors/toastError";
import { useDate } from "../../hooks/useDate";
import { useTimezone } from "../../hooks/useTimezone";

const reducer = (state, action) => {
  if (action.type === "LOAD_CAMPAIGNS") {
    const campaigns = action.payload;
    return Array.isArray(campaigns) ? campaigns : [];
  }

  if (action.type === "DELETE_CAMPAIGN") {
    const campaignId = action.payload;
    return state.filter((c) => c.id !== campaignId);
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

// D8.19.3 — Lógica (16 estados + useReducer + effects + handlers + API) de GroupCampaigns.
export default function useGroupCampaigns() {
  const { datetimeToClient } = useDate();
  const { formatDateTime, getConfiguredTimezone } = useTimezone();

  // scheduledDate de grupos se almacena naive (hora local de empresa, no UTC).
  // Parsear en TZ de empresa sin shift UTC (mismo patrón que campañas individuales).
  const formatGroupScheduledDate = React.useCallback((dateStr) => {
    if (!dateStr) return "";
    const tz = getConfiguredTimezone() || "America/Lima";
    const s = String(dateStr).replace("T", " ").split(".")[0];
    const m = moment.tz(s, "YYYY-MM-DD HH:mm:ss", tz);
    return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : formatDateTime(dateStr);
  }, [formatDateTime, getConfiguredTimezone]);

  const [loading, setLoading] = useState(true);
  const [campaigns, dispatch] = useReducer(reducer, []);
  const [searchParam, setSearchParam] = useState("");
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [selectedCampaignType, setSelectedCampaignType] = useState(null); // 'UNICA' o 'RECURRENTE'
  const [addMenuAnchorEl, setAddMenuAnchorEl] = useState(null);
  const [pauseModalOpen, setPauseModalOpen] = useState(false);
  const [stopModalOpen, setStopModalOpen] = useState(false);
  const [campaignToPause, setCampaignToPause] = useState(null);
  const [campaignToStop, setCampaignToStop] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(13); // ✅ Paginación iniciando en 13 (igual que Campaigns)
  const [executionsModalOpen, setExecutionsModalOpen] = useState(false);
  const [selectedCampaignForExecutions, setSelectedCampaignForExecutions] = useState(null);

  useEffect(() => {
    fetchCampaigns();
    
    // ✅ Polling cada 5 segundos para actualizar estados
    const intervalId = setInterval(() => {
      fetchCampaigns();
    }, 5000);
    
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // fetchCampaigns es estable, no necesita estar en dependencias

  const fetchCampaigns = async () => {
    try {
      const { data } = await listGroupCampaigns();
      
      // ✅ Validar que data sea un array
      if (!Array.isArray(data)) {
        dispatch({ type: "LOAD_CAMPAIGNS", payload: [] });
        setLoading(false);
        return;
      }
      
      // ✅ Filtrar campañas sin name
      const campaignsData = data.filter(c => c && c.name);
      dispatch({ type: "LOAD_CAMPAIGNS", payload: campaignsData });
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenCampaignModal = (campaignType = 'UNICA') => {
    setSelectedCampaign(null);
    setSelectedCampaignType(campaignType);
    setCampaignModalOpen(true);
  };

  const handleOpenAddMenu = (event) => {
    setAddMenuAnchorEl(event.currentTarget);
  };

  const handleCloseAddMenu = () => {
    setAddMenuAnchorEl(null);
  };

  const handleCloseCampaignModal = () => {
    setSelectedCampaign(null);
    setSelectedCampaignType(null);
    setCampaignModalOpen(false);
    fetchCampaigns(); // Recargar datos después de cerrar
  };

  const handleEditCampaign = async (campaign) => {
    try {
      // ✅ CORRECCIÓN CRÍTICA: Cargar datos completos de la campaña desde el backend
      // La lista solo tiene datos básicos, necesitamos todos los campos para editar
      const { data } = await getGroupCampaign(campaign.id);
      
      // ✅ CORRECCIÓN DE BUG: Usar originalGroupIds como fuente de verdad, no shippings
      // ✅ CRÍTICO: Para campañas recurrentes, shippings contiene TODOS los registros de todas las ejecuciones
      // Por eso debemos usar originalGroupIds como fuente de verdad, no shippings
      let groupIds = [];
      if (data.originalGroupIds) {
        try {
          groupIds = JSON.parse(data.originalGroupIds);
        } catch (e) {
          console.warn(`[GroupCampaigns] Error parseando originalGroupIds:`, e);
        }
      } else if (data.shippings && Array.isArray(data.shippings) && data.shippings.length > 0) {
        // Fallback: usar shippings pero eliminar duplicados
        groupIds = [...new Set(data.shippings
          .map(shipping => shipping.groupId || shipping.groupDetails?.groupId)
          .filter(id => id))];
      }
      
      // ✅ Preparar datos completos para el modal
      const campaignData = {
        ...data,
        groupIds, // Agregar groupIds para el selector de grupos
        whatsappId: data.whatsappId || data.whatsapp?.id,
        // ✅ Asegurar que scheduledDate esté en formato correcto
        scheduledDate: data.scheduledDate || data.nextScheduledAt,
        // ✅ Para campañas RECURRENTES activas, usar nextScheduledAt como scheduledDate
        // ✅ REGLA DE NEGOCIO: Solo usar data.tipo - No puede haber UNICA con recurrencia
        ...(data.tipo === 'GRUPO_RECURRENTE' && data.nextScheduledAt ? {
          scheduledDate: data.nextScheduledAt
        } : {}),
        // ✅ CRÍTICO: Mapear horarios desde hourlySchedules a recurrenceHourlySchedules
        // El backend devuelve hourlySchedules como array de objetos con time, message1-5, etc.
        recurrenceHourlySchedules: data.hourlySchedules && Array.isArray(data.hourlySchedules) && data.hourlySchedules.length > 0
          ? data.hourlySchedules.map(schedule => ({
              time: schedule.time,
              message1: schedule.message1 || "",
              message2: schedule.message2 || "",
              message3: schedule.message3 || "",
              message4: schedule.message4 || "",
              message5: schedule.message5 || "",
              mediaPath: schedule.mediaPath || null,
              mediaName: schedule.mediaName || null
            }))
          : (data.recurrenceHourlySchedules || [])
      };
      
      setSelectedCampaign(campaignData);
      setCampaignModalOpen(true);
    } catch (err) {
      console.error(`[GroupCampaigns] Error cargando campaña ${campaign.id}:`, err);
      toastError(err);
    }
  };

  const handleCopyCampaign = async (campaign) => {
    try {
      // Obtener datos completos de la campaña
      const { data } = await getGroupCampaign(campaign.id);
      
      console.log("📋 [handleCopyCampaign] Datos obtenidos de la API:", data);
      console.log("📋 [handleCopyCampaign] Shippings:", data.shippings);
      console.log("📋 [handleCopyCampaign] FileListId:", data.fileListId);
      
      // Crear objeto con datos copiados, limpiando fecha programada
      const copiedData = {
        ...data,
        id: undefined, // No copiar ID
        name: `${data.name} - Copia`,
        scheduledDate: "",
        status: "scheduled",
        sentGroups: 0,
        failedGroups: 0,
        startDate: null,
        endDate: null,
        shippings: data.shippings || [], // ✅ Incluir shippings (grupos)
        fileListId: data.fileListId || "", // ✅ Incluir fileListId
        mediaPath: data.mediaPath || null, // ✅ Incluir mediaPath
        mediaName: data.mediaName || null, // ✅ Incluir mediaName
        // ✅ CAMPOS DE RECURRENCIA - Resetear contadores pero mantener configuración
        executionCount: 0, // Resetear contador
        nextScheduledAt: null, // Resetear próxima ejecución
        lastExecutedAt: null, // Resetear última ejecución
        completedAt: null, // Resetear fecha de completado
      };
      
      console.log("📋 [handleCopyCampaign] Datos copiados preparados:", copiedData);
      console.log("📋 [handleCopyCampaign] Total grupos a copiar:", copiedData.shippings?.length || 0);
      
      // Establecer campaña para copiar
      setSelectedCampaign(copiedData);
      setCampaignModalOpen(true);
      
      toast.success("Campaña copiada. Revise y ajuste el nombre antes de crear.");
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await deleteGroupCampaign(campaignId);
      dispatch({ type: "DELETE_CAMPAIGN", payload: campaignId });
      toast.success("Campaña eliminada exitosamente");
    } catch (err) {
      toastError(err);
    }
    setDeletingCampaign(null);
  };

  const handlePauseCampaign = (campaign) => {
    setCampaignToPause(campaign);
    setPauseModalOpen(true);
  };

  const confirmPauseCampaign = async () => {
    if (!campaignToPause) return;
    
    try {
      await cancelGroupCampaign(campaignToPause.id);
      toast.success("Campaña pausada exitosamente");
      fetchCampaigns();
      setPauseModalOpen(false);
      setCampaignToPause(null);
    } catch (err) {
      toastError(err);
    }
  };

  const handleStopCampaign = (campaign) => {
    setCampaignToStop(campaign);
    setStopModalOpen(true);
  };

  const confirmStopCampaign = async () => {
    if (!campaignToStop) return;
    
    try {
      await stopGroupCampaign(campaignToStop.id);
      toast.success("Campaña parada definitivamente");
      fetchCampaigns();
      setStopModalOpen(false);
      setCampaignToStop(null);
    } catch (err) {
      toastError(err);
    }
  };

  const restartCampaign = async (campaign) => {
    try {
      await restartGroupCampaign(campaign.id);
      toast.success("Campaña reanudada exitosamente");
      fetchCampaigns();
    } catch (err) {
      toastError(err);
    }
  };

  return {
    addMenuAnchorEl,
    campaignModalOpen,
    campaignToPause,
    campaignToStop,
    campaigns,
    confirmModalOpen,
    confirmPauseCampaign,
    confirmStopCampaign,
    datetimeToClient,
    deletingCampaign,
    dispatch,
    executionsModalOpen,
    fetchCampaigns,
    formatDateTime,
    formatGroupScheduledDate,
    getConfiguredTimezone,
    handleCloseAddMenu,
    handleCloseCampaignModal,
    handleCopyCampaign,
    handleDeleteCampaign,
    handleEditCampaign,
    handleOpenAddMenu,
    handleOpenCampaignModal,
    handlePauseCampaign,
    handleSearch,
    handleStopCampaign,
    loading,
    page,
    pauseModalOpen,
    restartCampaign,
    rowsPerPage,
    searchParam,
    selectedCampaign,
    selectedCampaignForExecutions,
    selectedCampaignType,
    setAddMenuAnchorEl,
    setCampaignModalOpen,
    setCampaignToPause,
    setCampaignToStop,
    setConfirmModalOpen,
    setDeletingCampaign,
    setExecutionsModalOpen,
    setLoading,
    setPage,
    setPauseModalOpen,
    setRowsPerPage,
    setSearchParam,
    setSelectedCampaign,
    setSelectedCampaignForExecutions,
    setSelectedCampaignType,
    setStopModalOpen,
    stopModalOpen,
  };
}
